import { FC } from 'react';
import { useRouteError } from 'react-router-dom';

import { Alert, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';

import Response from '@/shared/types/response';

function isReactError(error: unknown): error is Error {
  return typeof error === 'object' && error !== null && 'message' in error;
}

function isCustomError(error: unknown): error is Response<unknown> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'msg' in error &&
    'status' in error
  );
}

const ErrorBoundry: FC = () => {
  const error = useRouteError();

  let componentToRender = null;

  if (isCustomError(error)) {
    componentToRender = (
      <>
        <Typography variant='h2'>{error.status || '500'}</Typography>
        <Typography variant='h4' mb={3}>
          Something went wrong
        </Typography>

        <Alert severity='error'>{error.msg}</Alert>
      </>
    );
  } else if (isReactError(error)) {
    componentToRender = (
      <>
        <Typography variant='h3'>An unexpected error occurred</Typography>
        <Typography variant='h4' pb={3}>
          please contact support
        </Typography>

        <Alert severity='error' sx={{ textAlign: 'left' }}>
          {error.stack}
        </Alert>
      </>
    );
  }

  return (
    <Stack
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: '50vw', textAlign: 'center' }}>
        {componentToRender}
      </Box>
    </Stack>
  );
};

export default ErrorBoundry;
